import type { FacetFilter } from '../googleSearch/types';

type FacetValue = string | number | [number, number];

const FIELD_NAME_MAP: Record<string, string> = {
  Brand: 'brands',
  Category: 'categories',
  'Primary Category': 'categories',
  Price: 'price',
  Condition: 'conditions',
  Colour: 'colorFamilies',
  Color: 'colorFamilies',
  'Sold by': 'Sold by',
};

const toRetailField = (facetName: string): string => {
  return FIELD_NAME_MAP[facetName] ?? `attributes.${facetName}`;
};

const escapeFilterValue = (value: string): string => {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
};

/** Escape unescaped inch-mark `"` inside a pre-built filter expression (e.g. `48"-50"` → `48\"-50\"`). */
export const sanitizeFilterQuotes = (filter: string): string => filter.replace(/(\d)"(?=[^,)\s])/g, '$1\\"');

/**
 * Converts FacetFilter[] into a Google Retail API filter expression string.
 * @see https://cloud.google.com/retail/docs/filter-and-order#filter
 *
 * Examples:
 *   brands: ANY("SONY", "APPLE") AND price: IN(100i, 500i)
 */
export const buildFilterExpression = (facets: Array<FacetFilter>): string => {
  if (!facets.length) return '';

  const grouped: Record<string, Array<FacetValue>> = {};
  for (const { name, value } of facets) {
    if (!grouped[name]) grouped[name] = [];
    grouped[name].push(value);
  }

  const expressions: Array<string> = [];

  Object.keys(grouped).forEach(name => {
    const values = grouped[name];
    const field = toRetailField(name);
    const rangeTuple = values.find((v: FacetValue): v is [number, number] => Array.isArray(v));

    if (rangeTuple) {
      const [min, max] = rangeTuple;
      const lower = min > 0 ? `${min}i` : '*';
      const upper = Number.isFinite(max) && max > 0 ? `${max}i` : '*';
      if (lower !== '*' || upper !== '*') {
        expressions.push(`${field}: IN(${lower}, ${upper})`);
      }
    } else {
      const quoted = values.map((v: FacetValue) => `"${escapeFilterValue(String(v))}"`).join(', ');
      expressions.push(`${field}: ANY(${quoted})`);
    }
  });

  return expressions.join(' AND ');
};
